import { create } from "zustand";

import { fetchServerVersion, versionSeen } from "./environment";

/** Nouvelle version de Generex mise en ligne pendant qu'un onglet est ouvert (audit de persistance m14). Le numéro de
 *  version du client est posé à la construction par deploy/vps/deploy.sh (`VITE_GENEREX_BUILD`), le même que celui
 *  que le serveur annonce après son redémarrage (`GET /api/version`) ; « dev » hors déploiement, jamais comparé. L'onglet compare les deux
 *  toutes les 5 minutes et à chaque retour sur la page (au plus une fois par 30 s), et propose de recharger. */

export const CLIENT_BUILD = (import.meta.env.VITE_GENEREX_BUILD as string | undefined)?.trim() || "dev";
/** Intervalle des comparaisons, et délai minimal entre deux comparaisons au retour sur la page. */
export const CHECK_EVERY_MS = 5 * 60_000;
export const MIN_GAP_MS = 30_000;

/** Le serveur sert une version plus récente que celle de ce client. Les numéros commencent par la date de la mise en
 *  ligne (`AAAAMMJJhhmmss-révision`) : le plus récent est le plus grand. Pendant un déploiement, le nouveau client
 *  peut être servi avant le redémarrage de l'API : un client plus récent que le serveur n'est pas signalé. */
export function newerBuild(server: string | null | undefined, client: string = CLIENT_BUILD): boolean {
  const remote = (server ?? "").trim();
  return Boolean(remote) && remote !== "dev" && client !== "dev" && remote > client;
}

interface VersionState {
  /** Version annoncée par le serveur quand elle diffère de celle du client. */
  available: string | null;
  /** Version dont l'avis a été écarté (« Plus tard ») : il ne revient qu'avec une version suivante. */
  dismissed: string | null;
  found: (build: string) => void;
  dismiss: () => void;
}

export const useVersion = create<VersionState>((set) => ({
  available: null,
  dismissed: null,
  found: (build) => set({ available: build }),
  dismiss: () => set((s) => ({ dismissed: s.available })),
}));

/** Avis à montrer : une version plus récente, pas écartée. */
export const noticeShown = (s: Pick<VersionState, "available" | "dismissed">): boolean =>
  Boolean(s.available) && s.available !== s.dismissed;

/** Version servie (`GET /api/version`), null si le serveur ne répond pas (mise en ligne en cours : nouvelle
 *  comparaison plus tard). La même réponse tient à jour l'environnement (préproduction, registre F35). */
export async function fetchServerBuild(): Promise<string | null> {
  const version = await fetchServerVersion();
  versionSeen(version);
  return typeof version?.build === "string" ? version.build : null;
}

export interface WatchDeps {
  fetchBuild: () => Promise<string | null>;
  client: string;
  now: () => number;
  every: (run: () => void, ms: number) => () => void;
  /** Retour sur la page (onglet de nouveau visible, fenêtre remise au premier plan) ; rend le désabonnement. */
  onReturn: (run: () => void) => () => void;
  found: (build: string) => void;
}

/** Comparaisons périodiques et au retour sur la page ; rend l'arrêt. Une comparaison à la fois. */
export function watchVersion(deps: WatchDeps): () => void {
  let last = -Infinity;
  let busy = false;
  const check = async (force = false) => {
    if (busy || (!force && deps.now() - last < MIN_GAP_MS)) return;
    busy = true;
    last = deps.now();
    try {
      const build = await deps.fetchBuild();
      if (newerBuild(build, deps.client)) deps.found(build as string);
    } finally {
      busy = false;
    }
  };
  const stopTimer = deps.every(() => void check(true), CHECK_EVERY_MS);
  const stopReturn = deps.onReturn(() => void check());
  return () => {
    stopTimer();
    stopReturn();
  };
}

/** Surveillance de l'application (navigateur). */
export function startVersionWatch(): () => void {
  if (typeof window === "undefined" || CLIENT_BUILD === "dev") return () => {};
  return watchVersion({
    fetchBuild: fetchServerBuild,
    client: CLIENT_BUILD,
    now: Date.now,
    every: (run, ms) => {
      const id = window.setInterval(run, ms);
      return () => window.clearInterval(id);
    },
    onReturn: (run) => {
      const visible = () => {
        if (document.visibilityState === "visible") run();
      };
      document.addEventListener("visibilitychange", visible);
      window.addEventListener("focus", run);
      return () => {
        document.removeEventListener("visibilitychange", visible);
        window.removeEventListener("focus", run);
      };
    },
    found: (build) => useVersion.getState().found(build),
  });
}

/** « Recharger » : saisies en cours de l'OPO envoyées d'abord (`flush`), puis rechargement. Les brouillons des
 *  Données initiales et les copies locales de l'OPO restent sur ce poste et sont repris à la réouverture. */
export async function reloadSafely(flush: () => Promise<unknown>, reload: () => void): Promise<void> {
  try {
    await flush();
  } catch {
    // envoi impossible : les copies locales restent sur ce poste, rechargement quand même
  }
  reload();
}
